import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, Save, Trash2, FileText, Edit2, Copy, 
  CheckCircle, AlertCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available placeholders for offer letters
const AVAILABLE_PLACEHOLDERS = [
  { key: '{{candidate_name}}', label: 'Candidate Name', category: 'Candidate' },
  { key: '{{candidate_email}}', label: 'Candidate Email', category: 'Candidate' },
  { key: '{{candidate_phone}}', label: 'Candidate Phone', category: 'Candidate' },
  { key: '{{position}}', label: 'Position', category: 'Job Details' },
  { key: '{{department}}', label: 'Department', category: 'Job Details' },
  { key: '{{client_company}}', label: 'Client Company', category: 'Job Details' },
  { key: '{{salary}}', label: 'Salary (with currency)', category: 'Compensation' },
  { key: '{{salary_amount}}', label: 'Salary Amount', category: 'Compensation' },
  { key: '{{currency}}', label: 'Currency', category: 'Compensation' },
  { key: '{{joining_date}}', label: 'Joining Date', category: 'Job Details' },
  { key: '{{contract_duration}}', label: 'Contract Duration', category: 'Job Details' },
  { key: '{{benefits}}', label: 'Benefits', category: 'Compensation' },
  { key: '{{special_conditions}}', label: 'Special Conditions', category: 'Other' },
  { key: '{{company_name}}', label: 'Company Name', category: 'Company' },
  { key: '{{date}}', label: 'Current Date', category: 'Other' },
  { key: '{{year}}', label: 'Current Year', category: 'Other' },
];

// Default template content
const DEFAULT_TEMPLATE = `<div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px;">
  <div style="text-align: center; margin-bottom: 40px;">
    <h1 style="color: #1e40af; margin-bottom: 10px;">OFFER OF EMPLOYMENT</h1>
    <p style="color: #6b7280;">{{company_name}}</p>
  </div>

  <p style="margin-bottom: 20px;"><strong>Date:</strong> {{date}}</p>

  <p style="margin-bottom: 20px;">
    Dear <strong>{{candidate_name}}</strong>,
  </p>

  <p style="margin-bottom: 20px;">
    We are pleased to offer you the position of <strong>{{position}}</strong> in the <strong>{{department}}</strong> 
    department at <strong>{{client_company}}</strong>. This offer is subject to the terms and conditions outlined below.
  </p>

  <div style="background: #f3f4f6; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #1e40af; margin-bottom: 15px;">Employment Details</h3>
    <table style="width: 100%;">
      <tr><td style="padding: 8px 0;"><strong>Position:</strong></td><td>{{position}}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Department:</strong></td><td>{{department}}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Client:</strong></td><td>{{client_company}}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Start Date:</strong></td><td>{{joining_date}}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Contract Type:</strong></td><td>{{contract_duration}}</td></tr>
    </table>
  </div>

  <div style="background: #ecfdf5; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
    <h3 style="color: #059669; margin-bottom: 15px;">Compensation Package</h3>
    <table style="width: 100%;">
      <tr><td style="padding: 8px 0;"><strong>Monthly Salary:</strong></td><td>{{salary}}</td></tr>
      <tr><td style="padding: 8px 0;"><strong>Benefits:</strong></td><td>{{benefits}}</td></tr>
    </table>
  </div>

  <div style="margin-bottom: 20px;">
    <h3 style="color: #1e40af; margin-bottom: 10px;">Special Conditions</h3>
    <p>{{special_conditions}}</p>
  </div>

  <p style="margin-bottom: 20px;">
    Please confirm your acceptance of this offer by signing below. We look forward to welcoming you to our team.
  </p>

  <div style="margin-top: 40px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
    <div style="display: flex; justify-content: space-between;">
      <div>
        <p><strong>Candidate Signature:</strong></p>
        <div style="border-bottom: 1px solid #000; width: 200px; height: 40px; margin-top: 10px;"></div>
        <p style="margin-top: 5px;">{{candidate_name}}</p>
        <p style="color: #6b7280; font-size: 12px;">Date: _______________</p>
      </div>
      <div>
        <p><strong>Authorized Signatory:</strong></p>
        <div style="border-bottom: 1px solid #000; width: 200px; height: 40px; margin-top: 10px;"></div>
        <p style="margin-top: 5px;">{{company_name}}</p>
        <p style="color: #6b7280; font-size: 12px;">Date: _______________</p>
      </div>
    </div>
  </div>
</div>`;

export default function OfferTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: DEFAULT_TEMPLATE,
    is_default: false
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/onboarding/templates`, {
        headers: getAuthHeaders()
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }

    try {
      // Extract placeholders from content
      const placeholderRegex = /\{\{[^}]+\}\}/g;
      const foundPlaceholders = formData.content.match(placeholderRegex) || [];
      const uniquePlaceholders = [...new Set(foundPlaceholders)];

      const payload = {
        ...formData,
        placeholders: uniquePlaceholders
      };

      if (editingTemplate) {
        await axios.put(
          `${API_URL}/api/onboarding/templates/${editingTemplate.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Template updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/onboarding/templates`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Template created successfully');
      }

      fetchTemplates();
      resetForm();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await axios.delete(`${API_URL}/api/onboarding/templates/${templateId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      content: template.content,
      is_default: template.is_default
    });
    setShowEditor(true);
  };

  const insertPlaceholder = (placeholder) => {
    const textarea = document.getElementById('template-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = formData.content.substring(0, start) + placeholder + formData.content.substring(end);
      setFormData({ ...formData, content: newContent });
    } else {
      setFormData({ ...formData, content: formData.content + placeholder });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      content: DEFAULT_TEMPLATE,
      is_default: false
    });
    setEditingTemplate(null);
    setShowEditor(false);
    setPreviewMode(false);
  };

  const getPreviewContent = () => {
    let preview = formData.content;
    const sampleData = {
      '{{candidate_name}}': 'John Smith',
      '{{candidate_email}}': 'john.smith@email.com',
      '{{candidate_phone}}': '+60 12-345 6789',
      '{{position}}': 'Senior Software Engineer',
      '{{department}}': 'Engineering',
      '{{client_company}}': 'TechCorp Solutions',
      '{{salary}}': 'MYR 12,000.00',
      '{{salary_amount}}': '12,000.00',
      '{{currency}}': 'MYR',
      '{{joining_date}}': '1 March 2025',
      '{{contract_duration}}': 'Permanent',
      '{{benefits}}': 'Medical insurance, Annual leave, EPF contribution',
      '{{special_conditions}}': 'Subject to successful background verification',
      '{{company_name}}': 'Aurous Consultancy Sdn Bhd',
      '{{date}}': new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
      '{{year}}': new Date().getFullYear().toString()
    };

    for (const [key, value] of Object.entries(sampleData)) {
      preview = preview.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    }
    return preview;
  };

  // Group placeholders by category
  const groupedPlaceholders = AVAILABLE_PLACEHOLDERS.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offer-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Offer Letter Templates</h1>
          <p className="text-muted-foreground">Create and manage offer letter templates with auto-fill placeholders</p>
        </div>
        {!showEditor && (
          <Button onClick={() => setShowEditor(true)} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </Button>
        )}
      </div>

      {/* Template Editor */}
      {showEditor && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Standard Employment Offer"
                  data-testid="template-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={formData.is_default}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_default">Set as default template</Label>
            </div>

            {/* Placeholders Panel */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Available Placeholders (click to insert)
              </h4>
              <div className="space-y-3">
                {Object.entries(groupedPlaceholders).map(([category, placeholders]) => (
                  <div key={category}>
                    <p className="text-xs text-muted-foreground mb-1">{category}</p>
                    <div className="flex flex-wrap gap-2">
                      {placeholders.map((p) => (
                        <Badge
                          key={p.key}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
                          onClick={() => insertPlaceholder(p.key)}
                        >
                          {p.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Editor / Preview Toggle */}
            <div className="flex items-center gap-2 mb-2">
              <Button
                variant={!previewMode ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewMode(false)}
              >
                <Edit2 className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button
                variant={previewMode ? "default" : "outline"}
                size="sm"
                onClick={() => setPreviewMode(true)}
              >
                <Eye className="w-4 h-4 mr-1" />
                Preview
              </Button>
            </div>

            {/* Content */}
            {!previewMode ? (
              <div className="space-y-2">
                <Label htmlFor="template-content">Template Content (HTML)</Label>
                <Textarea
                  id="template-content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter your offer letter template here..."
                  className="min-h-[400px] font-mono text-sm"
                  data-testid="template-content-input"
                />
              </div>
            ) : (
              <div className="border rounded-lg p-4 bg-white min-h-[400px]">
                <div 
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t">
              <Button onClick={handleSaveTemplate} data-testid="save-template-btn">
                <Save className="w-4 h-4 mr-2" />
                {editingTemplate ? 'Update Template' : 'Save Template'}
              </Button>
              <Button variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Templates List */}
      {!showEditor && (
        <div className="grid gap-4">
          {templates.length === 0 ? (
            <Card className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No templates yet</h3>
              <p className="text-muted-foreground mb-4">Create your first offer letter template to get started</p>
              <Button onClick={() => setShowEditor(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </Card>
          ) : (
            templates.map((template) => (
              <Card key={template.id} className="hover:border-primary/30 transition-colors" data-testid={`template-${template.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">{template.name}</h3>
                        {template.is_default && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Default
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{template.placeholders?.length || 0} placeholders</span>
                        <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditTemplate(template)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
